import { Button, Col, Layout, notification, Row, Select, Space, Spin, Typography } from 'antd';
import * as React from 'react';
import moment from 'moment';
import { Editor } from '@tinymce/tinymce-react';
import AdminEmailTemplatesApiService from '../../api/AdminEmailTemplatesApiService';
import LookupsApiService from '../../api/LookupsApiService';
import EMAIL_TEMPLATE_PARAMS, { EmailTemplateParam } from '../../consts/EmailTemplateParams';
import LookupTableDTO from '../../models/LookupTableDTO';
import SaveEmailTemplateDTO from '../../models/SaveEmailTemplateDTO';
import CurrentUser from '../../utils/CurrentUser';

const { Content } = Layout;
const { Option } = Select;
const { Text, Title } = Typography;

interface ManageEmailTemplatesProps { }

interface ManageEmailTemplatesState {
  templateTypes: LookupTableDTO[];
  selectedTypeId: number | null;
  editorContent: string;
  loadingTypes: boolean;
  loadingTemplate: boolean;
  editorReady: boolean;
  saving: boolean;
  previewHtml: string | null;
  lastSavedOn: moment.Moment | string | null;
  lastSavedBy: string | null;
}

class ManageEmailTemplates extends React.Component<ManageEmailTemplatesProps, ManageEmailTemplatesState> {
  private currentParams: EmailTemplateParam[] = [];
  private previewTimer: ReturnType<typeof setTimeout> | null = null;
  private tinyMceEditor: any;

  constructor(props: ManageEmailTemplatesProps) {
    super(props);
    this.state = {
      templateTypes: [],
      selectedTypeId: null,
      editorContent: '',
      loadingTypes: true,
      loadingTemplate: false,
      editorReady: false,
      saving: false,
      previewHtml: null,
      lastSavedOn: null,
      lastSavedBy: null,
    };
  }

  componentWillUnmount() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  componentDidMount() {
    LookupsApiService.getEmailTemplateTypes()
      .then(templateTypes => {
        this.setState({ templateTypes, loadingTypes: false });
      })
      .catch(() => {
        this.setState({ loadingTypes: false });
        notification.error({ message: 'Failed to load email template types.' });
      });
  }

  private getParamsForType(typeId: number | null): EmailTemplateParam[] {
    if (!typeId) return [];
    return EMAIL_TEMPLATE_PARAMS[typeId] || [];
  }

  private toEditorContent = (html: string): string => {
    return html.replace(/\{\{(\w+)\}\}/g, (_, param) => {
      return `<span class="mceNonEditable email-param">{{${param}}}</span>`;
    });
  };

  private fromEditorContent = (html: string): string => {
    return html.replace(
      /<span[^>]*class="[^"]*email-param[^"]*"[^>]*>\{\{(\w+)\}\}<\/span>/g,
      (_, param) => `{{${param}}}`
    );
  };

  private onTypeChange = (typeId: number) => {
    this.currentParams = this.getParamsForType(typeId);
    this.setState({ selectedTypeId: typeId, loadingTemplate: true, previewHtml: null, lastSavedOn: null, lastSavedBy: null }, () => {
      AdminEmailTemplatesApiService.get(typeId)
        .then(dto => {
          const rawHtml = dto?.htmlContent ?? '';
          const content = rawHtml ? this.toEditorContent(rawHtml) : '';
          console.log(moment(dto.createdOn));
          this.setState(
            {
              editorContent: content,
              loadingTemplate: false,
              lastSavedOn: dto?.createdOn ? moment.utc(dto.createdOn).local() : null,
              lastSavedBy: dto?.createdBy ?? null,
            },
            () => {
              if (this.tinyMceEditor && this.tinyMceEditor.undoManager) {
                this.tinyMceEditor.undoManager.clear();
              }
            });
          return AdminEmailTemplatesApiService.preview(typeId, {
            emailTemplateTypeId: typeId,
            htmlContent: rawHtml,
          });
        })
        .then(html => {
          this.setState({ previewHtml: html });
        })
        .catch(() => {
          this.setState({ loadingTemplate: false });
          notification.error({ message: 'Failed to load template.' });
        });
    });
  };

  private onEditorChange = (content: string) => {
    this.setState({ editorContent: content });

    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => {
      const { selectedTypeId } = this.state;
      if (!selectedTypeId) return;

      AdminEmailTemplatesApiService.preview(selectedTypeId, {
        emailTemplateTypeId: selectedTypeId,
        htmlContent: this.fromEditorContent(content),
      })
        .then(html => {
          this.setState({ previewHtml: html });
        })
        .catch(() => {
          notification.error({ message: 'Failed to generate preview.' });
        });
    }, 1500);
  };

  private onSave = () => {
    const { selectedTypeId, editorContent } = this.state;
    if (!selectedTypeId) return;

    const model: SaveEmailTemplateDTO = {
      emailTemplateTypeId: selectedTypeId,
      htmlContent: this.fromEditorContent(editorContent),
    };

    this.setState({ saving: true });
    AdminEmailTemplatesApiService.save(model)
      .then(result => {
        if (result?.succeeded) {
          notification.success({ message: 'Template saved successfully.' });
          this.setState({ lastSavedOn: moment(), lastSavedBy: CurrentUser.Get()?.userName || null });
        } else {
          notification.error({ message: 'Failed to save template.' });
        }
        this.setState({ saving: false });
      })
      .catch(() => {
        notification.error({ message: 'Failed to save template.' });
        this.setState({ saving: false });
      });
  };

  private sendTestEmail = () => {
    const { selectedTypeId } = this.state;
    if (!selectedTypeId) return;

    AdminEmailTemplatesApiService.sentTestEmail(selectedTypeId)
      .then(() => {
        notification.success({ message: 'Email sent.' });
      })
      .catch(() => {
        notification.error({ message: 'Failed to send email.' });
      });
  };

  render() {
    const {
      templateTypes,
      selectedTypeId,
      editorContent,
      loadingTypes,
      loadingTemplate,
      editorReady,
      saving,
      previewHtml,
      lastSavedOn,
      lastSavedBy,
    } = this.state;

    return (
      <Content className="content-pad">
        <Title level={3}>Email Templates</Title>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Template Type</label>
          <Select
            style={{ width: 360 }}
            placeholder="Select a template type"
            onChange={this.onTypeChange}
            value={selectedTypeId ?? undefined}
            loading={loadingTypes}
          >
            {templateTypes.map(t => (
              <Option key={t.id} value={t.id}>
                {t.name}
              </Option>
            ))}
          </Select>
        </div>

        {selectedTypeId && (
          <Spin spinning={loadingTemplate}>
            <Row gutter={[24, 16]}>
              <Col xs={24} xl={previewHtml ? 12 : 24}>
                <Spin spinning={!editorReady}>
                  <Editor
                    tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                    value={editorContent}
                    onEditorChange={this.onEditorChange}
                    onInit={() => this.setState({ editorReady: true })}
                    init={({
                      license_key: 'gpl',
                      height: 600,
                      promotion: false,
                      branding: false,
                      content_css: false,
                      plugins: 'link lists table code',
                      resize: false,
                      removed_menuitems: 'newdocument print',
                      toolbar_mode: 'wrap',
                      toolbar:
                        'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link table | code | insertparam',
                      noneditable_class: 'mceNonEditable',
                      content_style: `
                      .email-param {
                        background-color: #cfe8fc;
                        border-radius: 3px;
                        padding: 1px 4px;
                        font-family: monospace;
                        font-size: 0.9em;
                        cursor: default;
                      }
                    `,
                      setup: (editor: any) => {
                        this.tinyMceEditor = editor;

                        // Before applying inline formatting, replace email-param spans with
                        // plain text so TinyMCE's formatter can wrap them. After the command
                        // runs, find any {{param}} text nodes and re-wrap them as spans.
                        const INLINE_FORMAT_COMMANDS = [
                          'bold', 'italic', 'underline', 'strikethrough',
                          'forecolor', 'hilitecolor', 'fontsize', 'fontname', 'removeformat',
                          'mcetoggleformat',
                        ];
                        let spansRemoved = false;

                        const removeParamSpans = () => {
                          editor.getBody().querySelectorAll('.email-param').forEach((span: Element) => {
                            const text = editor.getDoc().createTextNode(span.textContent || '');
                            span.parentNode?.replaceChild(text, span);
                          });
                        };

                        const restoreParamSpans = () => {
                          const doc = editor.getDoc();
                          const walker = doc.createTreeWalker(editor.getBody(), NodeFilter.SHOW_TEXT, null);
                          const textNodes: Text[] = [];
                          let node: Node | null;
                          while ((node = walker.nextNode())) {
                            if (/\{\{\w+\}\}/.test((node as Text).textContent || '')) {
                              textNodes.push(node as Text);
                            }
                          }
                          textNodes.forEach((textNode: Text) => {
                            const parts = (textNode.textContent || '').split(/(\{\{\w+\}\})/);
                            if (parts.length <= 1) return;
                            const fragment = doc.createDocumentFragment();
                            parts.forEach((part: string) => {
                              if (/^\{\{\w+\}\}$/.test(part)) {
                                const span = doc.createElement('span');
                                span.className = 'mceNonEditable email-param';
                                span.setAttribute('contenteditable', 'false');
                                span.textContent = part;
                                fragment.appendChild(span);
                              } else if (part) {
                                fragment.appendChild(doc.createTextNode(part));
                              }
                            });
                            textNode.parentNode?.replaceChild(fragment, textNode);
                          });
                        };

                        editor.on('BeforeExecCommand', (e: any) => {
                          if (!INLINE_FORMAT_COMMANDS.includes(e.command.toLowerCase())) return;
                          const rng = editor.selection.getRng();
                          if (!rng || rng.collapsed) return;
                          removeParamSpans();
                          spansRemoved = true;
                        });

                        editor.on('ExecCommand', () => {
                          if (spansRemoved) {
                            restoreParamSpans();
                            spansRemoved = false;
                          }
                        });

                        editor.ui.registry.addMenuButton('insertparam', {
                          text: 'Insert Parameter',
                          fetch: (callback: any) => {
                            const items = this.currentParams.map((p: EmailTemplateParam) => ({
                              type: 'menuitem',
                              text: p.label,
                              onAction: () => {
                                editor.insertContent(
                                  `<span class="mceNonEditable email-param">{{${p.name}}}</span>`
                                );
                              },
                            }));
                            callback(items);
                          },
                        });
                      },
                    }) as any}
                  />
                </Spin>
                {lastSavedOn && (
                  <div style={{ marginTop: 4, textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Last saved: {moment(lastSavedOn).format('ddd MMM Do YYYY, h:mm a')}{lastSavedBy ? ` by ${lastSavedBy}` : ''}
                    </Text>
                  </div>
                )}
              </Col>

              {previewHtml && (
                <Col xs={24} xl={12}>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, height: '100%' }}>
                    <div style={{ padding: '8px 12px', background: '#fafafa', borderBottom: '1px solid #d9d9d9', fontWeight: 600 }}>
                      Email Preview
                    </div>
                    <div style={{ height: '560px', overflowY: 'hidden' }}>
                      <iframe
                        title="Email Preview"
                        sandbox="allow-same-origin"
                        srcDoc={previewHtml}
                        style={{ width: '100%', minHeight: 400, height: '100%', border: 'none' }}
                      />
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            <Space style={{ marginTop: 16 }}>
              <Button
                type="primary"
                loading={saving}
                onClick={this.onSave}
                disabled={!selectedTypeId}
              >
                Save
              </Button>
              <Button
                type="default"
                onClick={this.sendTestEmail}
                disabled={!selectedTypeId}
              >
                Send Test Email
              </Button>
            </Space>
          </Spin>
        )}
      </Content>
    );
  }
}

export default ManageEmailTemplates;
